import { QueryFunctionContext, useMutation, useQuery } from 'react-query'
import { castImmutable } from 'immer'
import apiClient from '../../utils/api'
import {
    TEmailDeliveryStatus,
    TFeatureFlagKey,
    TInternalDashboardSummary,
    TInternalEmailTestResult,
    TInternalEmailTests,
    TInternalFeatureFlags,
    TInternalUsage,
    TInternalUsageWindow,
} from '../../utils/types'
import {
    OptimisticTransaction,
    beginOptimisticTransaction,
    optimisticQueryChange,
    reconcileOptimisticTransaction,
    rollbackOptimisticTransaction,
} from '../optimisticTransaction'
import { useGTMutation, useGTQueryClient } from '../queryUtils'

export const useGetInternalDashboardSummary = () => {
    return useQuery<TInternalDashboardSummary>('internal_dashboard_summary', getInternalDashboardSummary, {
        refetchOnMount: true,
    })
}

const getInternalDashboardSummary = async ({ signal }: QueryFunctionContext) => {
    try {
        const res = await apiClient.get('/internal/summary/', { signal })
        return castImmutable(res.data)
    } catch {
        throw 'getInternalDashboardSummary failed'
    }
}

// Keeps the previous window on screen while the next one loads, so switching never flashes zeros.
export const useGetInternalUsage = (window: TInternalUsageWindow) =>
    useQuery<TInternalUsage>(['internal_usage', window], getInternalUsage, {
        refetchOnMount: true,
        keepPreviousData: true,
    })

const getInternalUsage = async ({ queryKey, signal }: QueryFunctionContext) => {
    const response = await apiClient.get('/internal/usage/', { params: { window: queryKey[1] }, signal })
    return castImmutable(response.data)
}

export const useGetInternalFeatureFlags = () =>
    useQuery<TInternalFeatureFlags>('internal_feature_flags', getInternalFeatureFlags, {
        refetchOnMount: true,
    })

const getInternalFeatureFlags = async ({ signal }: QueryFunctionContext) => {
    const response = await apiClient.get('/internal/feature_flags/', { signal })
    return castImmutable(response.data)
}

export const useUpdateInternalFeatureFlags = () => {
    const queryClient = useGTQueryClient()
    return useGTMutation<
        TInternalFeatureFlags,
        unknown,
        Partial<Record<TFeatureFlagKey, boolean>>,
        OptimisticTransaction<TInternalFeatureFlags>
    >(
        async (flags) => {
            const response = await apiClient.patch('/internal/feature_flags/', flags)
            return castImmutable(response.data)
        },
        {
            tag: 'internal_feature_flags',
            errorMessage: 'update feature flags',
            invalidateTagsOnSettled: [
                'internal_feature_flags',
                'feature_flags',
                'overview',
                'overview-supported-views',
            ],
            onMutate: (flags) => {
                void queryClient.cancelQueries('internal_feature_flags')
                return beginOptimisticTransaction(queryClient, [
                    optimisticQueryChange<TInternalFeatureFlags, TInternalFeatureFlags>(
                        'internal_feature_flags',
                        (current) => {
                            if (!current) return current
                            const next = { ...current }
                            for (const [untypedKey, enabled] of Object.entries(flags)) {
                                const key = untypedKey as TFeatureFlagKey
                                if (enabled === undefined || !next[key]) continue
                                next[key] = { ...next[key], enabled }
                            }
                            return next
                        },
                        { reconcile: (_current, data) => data }
                    ),
                ])
            },
            onSuccess: (data, _flags, transaction) => reconcileOptimisticTransaction(transaction, data),
            onError: (_error, _flags, transaction) => rollbackOptimisticTransaction(transaction),
        },
        false
    )
}

export const useGetInternalEmailTests = () =>
    useQuery<TInternalEmailTests>('internal_email_tests', getInternalEmailTests, {
        refetchOnMount: true,
    })

const getInternalEmailTests = async ({ signal }: QueryFunctionContext) => {
    const response = await apiClient.get('/internal/email_tests/', { signal })
    return castImmutable(response.data)
}

// Off pauses email to users; test sends to the admin still go out when the server can send.
export const useUpdateEmailDelivery = () => {
    const queryClient = useGTQueryClient()
    return useGTMutation<TEmailDeliveryStatus, unknown, boolean, OptimisticTransaction<TEmailDeliveryStatus>>(
        async (enabled) => {
            const response = await apiClient.patch('/internal/email_delivery/', { enabled })
            return castImmutable(response.data)
        },
        {
            tag: 'internal_email_tests',
            errorMessage: 'update email delivery',
            invalidateTagsOnSettled: ['internal_email_tests'],
            onMutate: (enabled) => {
                void queryClient.cancelQueries('internal_email_tests')
                return beginOptimisticTransaction(queryClient, [
                    optimisticQueryChange<TInternalEmailTests, TEmailDeliveryStatus>(
                        'internal_email_tests',
                        (current) =>
                            current
                                ? {
                                      ...current,
                                      delivery: {
                                          ...current.delivery,
                                          switched_on: enabled,
                                          enabled: enabled && current.delivery.config_issue === '',
                                      },
                                  }
                                : current,
                        {
                            reconcile: (current, delivery) => (current ? { ...current, delivery } : current),
                        }
                    ),
                ])
            },
            onSuccess: (delivery, _enabled, transaction) => reconcileOptimisticTransaction(transaction, delivery),
            onError: (_error, _enabled, transaction) => rollbackOptimisticTransaction(transaction),
        },
        false
    )
}

// type is an email key or 'all'. The recipient is always the signed-in admin, chosen by the server.
export const useSendInternalTestEmail = () =>
    useMutation<TInternalEmailTestResult[], unknown, string>(async (type) => {
        const response = await apiClient.post('/internal/email_tests/', { type })
        return response.data
    })
